import {
  fetchIncotermTimeline,
  pick,
} from '../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import { getAuthToken } from '../../../../../auth/tokenvalidation';
import { api } from '../../../../../config';
import {
  normalizeCargoRatioPricingFields,
  putCargoRatioPricing as putCargoRatioPricingShared,
} from '../../../../../shared/offer-domain/cargoRatio';
import {
  CARGO_RATIO_LABELS,
  fetchAllCargoRatioSelections,
  fetchCargoRatioOptions,
  fetchQuantityInformationTargets,
  filterSelectionsForParcel,
  formatParcelSizes,
  getCargoRatioKey,
} from '../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';

const LPG_UNIT = 'MT';

export const resolveLpgSourceParcelSize = row => {
  const toNum = value => {
    const n = Number(String(value ?? '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const fromRatio = toNum(row?.cargoRatioParcelSize);
  const fromBasis = toNum(row?.deliveryBasis);
  const fromParcel = toNum(row?.parcelSize);
  if (fromRatio != null && fromBasis != null && fromRatio < fromBasis) {
    return fromBasis;
  }
  return fromRatio ?? fromBasis ?? fromParcel;
};

const normalizeCargoRatioEntry = entry => {
  const normalized = normalizeCargoRatioPricingFields(entry);
  return {
    ...normalized,
    parcelSize: normalized.parcelSize ?? null,
    alpha: normalized.alpha ?? null,
    alphaImprovement: normalized.alphaImprovement ?? null,
    alphaHps: normalized.alphaHps ?? null,
    alphaDyedHps: normalized.alphaDyedHps ?? null,
    alphaUndyedHps: normalized.alphaUndyedHps ?? null,
  };
};

const collectCargoRatiosFromIncoterms = (rawItems = []) => {
  const byId = new Map();

  rawItems.forEach(raw => {
    const list = pick(raw, 'cargoRatios', 'CargoRatios') || [];
    (Array.isArray(list) ? list : []).forEach(entry => {
      const normalized = normalizeCargoRatioEntry(entry);
      if (
        normalized.id === null ||
        normalized.id === undefined ||
        normalized.id === ''
      ) {
        return;
      }
      if (
        normalized.quantityInformationFK === null ||
        normalized.quantityInformationFK === undefined ||
        normalized.quantityInformationFK === ''
      ) {
        return;
      }
      byId.set(String(normalized.id), normalized);
    });
  });

  return [...byId.values()];
};

const buildQuantityTargetsFromCargoRatios = (cargoRatios = []) => {
  const byFk = new Map();

  cargoRatios.forEach(ratio => {
    const fk = String(ratio.quantityInformationFK ?? '');
    if (!fk || byFk.has(fk)) return;
    byFk.set(fk, {
      quantityInformationFk: ratio.quantityInformationFK,
      quantity: ratio.parcelSize,
      cargoRatios: [],
    });
  });

  cargoRatios.forEach(ratio => {
    const fk = String(ratio.quantityInformationFK ?? '');
    const target = byFk.get(fk);
    if (!target) return;
    target.cargoRatios.push(ratio);
  });

  return [...byFk.values()].sort(
    (a, b) => Number(a.quantity) - Number(b.quantity)
  );
};

const resolveQuantityInformationFk = (item, quantityTargets = []) => {
  const directId = item?.quantityInformationId;
  if (
    directId !== null &&
    directId !== undefined &&
    directId !== '' &&
    quantityTargets.some(
      target => String(target.quantityInformationFk) === String(directId)
    )
  ) {
    return directId;
  }

  const parcelNumeric = Number(
    String(item?.parcelSize ?? item?.deliveryBasis ?? '').replace(
      /[^\d.-]/g,
      ''
    )
  );

  if (!Number.isFinite(parcelNumeric)) return directId ?? null;

  const matched = quantityTargets.find(
    target => Number(target.quantity) === parcelNumeric
  );

  return matched?.quantityInformationFk ?? directId ?? null;
};

const applyQuantityDeliveryParcelSize = (item, quantityTargets = []) => {
  const quantityInformationFk = resolveQuantityInformationFk(
    item,
    quantityTargets
  );
  const target = quantityTargets.find(
    entry =>
      String(entry.quantityInformationFk) === String(quantityInformationFk)
  );

  return {
    ...item,
    quantityInformationId: item.quantityInformationId ?? quantityInformationFk,
    parcelSize: target?.quantity ?? item.parcelSize,
    parcelSizeLabel: target
      ? `${formatParcelSizes([target], LPG_UNIT)[0]}`
      : null,
  };
};

const resolveRatioLabel = (ratio, ratioOptions = []) => {
  const ratioKey = getCargoRatioKey(ratio?.cargoRatio ?? ratio?.id);
  const option = ratioOptions.find(
    entry => getCargoRatioKey(entry.id) === ratioKey
  );
  if (option?.label) return option.label;

  const fromEntry = ratio?.cargoRatioLabel ?? ratio?.label;
  if (fromEntry) return fromEntry;

  const numeric = Number(ratioKey);
  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 5) {
    return CARGO_RATIO_LABELS[numeric - 1] ?? ratioKey;
  }
  return ratioKey;
};

const selectionsByQuantityFk = (
  quantityTargets = [],
  fallbackSelections = []
) => {
  const map = new Map();

  quantityTargets.forEach(target => {
    const fk = String(target.quantityInformationFk ?? '');
    if (!fk) return;
    const embedded = Array.isArray(target.cargoRatios)
      ? target.cargoRatios
      : [];
    if (embedded.length) {
      map.set(fk, embedded);
    }
  });

  if (map.size) return map;

  fallbackSelections.forEach(selection => {
    const fk = String(
      pick(selection, 'quantityInformationFK', 'QuantityInformationFK')
    );
    if (!fk) return;
    if (!map.has(fk)) map.set(fk, []);
    map.get(fk).push(normalizeCargoRatioEntry(selection));
  });

  return map;
};

const expandItemsWithCargoRatios = ({
  items,
  quantityTargets,
  ratiosByFk,
  ratioOptions,
  parcelSizeLabels,
  unitLabel,
}) => {
  const withParcelSize = items.map(item =>
    applyQuantityDeliveryParcelSize(item, quantityTargets)
  );

  if (
    ![...ratiosByFk.values()].some(list => Array.isArray(list) && list.length)
  ) {
    return withParcelSize.map(item => ({
      ...item,
      quantityInformationCargoRatioId: null,
      cargoRatio: null,
      cargoRatioLabel: null,
      parcelSizeLabels,
    }));
  }

  return withParcelSize.flatMap(item => {
    const fk = String(item.quantityInformationId ?? '');
    const ratios = ratiosByFk.get(fk) || [];

    if (item.quantityInformationCargoRatioId) {
      const matchedRatio = ratios.find(
        r => r.id === item.quantityInformationCargoRatioId
      );
      if (matchedRatio) {
        const parcelSizeValue =
          matchedRatio.parcelSize !== null &&
          matchedRatio.parcelSize !== undefined
            ? matchedRatio.parcelSize
            : item.parcelSize;
        const cargoRatioSnapshot =
          normalizeCargoRatioPricingFields(matchedRatio);
        return [
          {
            ...item,
            parcelSize: parcelSizeValue,
            parcelSizeLabel: parcelSizeValue
              ? formatParcelSizes(
                  [{ quantity: parcelSizeValue }],
                  unitLabel || LPG_UNIT
                )[0]
              : item.parcelSizeLabel,
            alpha:
              matchedRatio.alpha !== null &&
              matchedRatio.alpha !== undefined &&
              matchedRatio.alpha !== ''
                ? matchedRatio.alpha
                : item.alpha,
            cargoRatioParcelSize: matchedRatio.parcelSize ?? null,
            cargoRatioAlpha: matchedRatio.alpha ?? null,
            cargoRatioAlphaImprovement: matchedRatio.alphaImprovement ?? null,
            improvementAlpha:
              matchedRatio.alphaImprovement ?? item.improvementAlpha ?? null,
            alphaHps: matchedRatio.alphaHps ?? item.alphaHps ?? null,
            alphaDyedHps:
              matchedRatio.alphaDyedHps ?? item.alphaDyedHps ?? null,
            alphaUndyedHps:
              matchedRatio.alphaUndyedHps ?? item.alphaUndyedHps ?? null,
            cargoRatioSnapshot,
            parcelSizeLabels,
          },
        ];
      }
      return [{ ...item, parcelSizeLabels }];
    }

    const itemIncotermId = String(item.quantityIncotermId ?? '');
    const filteredRatios = itemIncotermId
      ? ratios.filter(
          r => String(r.quantityIncotermFK ?? '') === itemIncotermId
        )
      : ratios;

    if (!filteredRatios.length) {
      return [
        {
          ...item,
          quantityInformationCargoRatioId: null,
          cargoRatio: null,
          cargoRatioLabel: null,
          parcelSizeLabels,
        },
      ];
    }

    return filteredRatios.map(ratio => {
      const ratioValue = ratio.cargoRatio ?? ratio.id;
      const ratioKey = getCargoRatioKey(ratioValue);
      const option = ratioOptions.find(
        entry => getCargoRatioKey(entry.id) === ratioKey
      );
      const parcelSizeValue = resolveLpgSourceParcelSize({
        cargoRatioParcelSize: ratio.parcelSize,
        deliveryBasis: item.deliveryBasis,
        parcelSize: item.parcelSize,
      });
      const alphaValue =
        ratio.alpha !== null && ratio.alpha !== undefined && ratio.alpha !== ''
          ? ratio.alpha
          : '';
      const cargoRatioSnapshot = normalizeCargoRatioPricingFields(ratio);

      return {
        ...item,
        quantityInformationCargoRatioId: ratio.id ?? null,
        cargoRatio: option?.id ?? ratioValue,
        cargoRatioLabel: resolveRatioLabel(ratio, ratioOptions),
        parcelSize: parcelSizeValue,
        parcelSizeLabel: parcelSizeValue
          ? formatParcelSizes(
              [{ quantity: parcelSizeValue }],
              unitLabel || LPG_UNIT
            )[0]
          : item.parcelSizeLabel,
        alpha: alphaValue,
        cargoRatioParcelSize: parcelSizeValue,
        cargoRatioAlpha: ratio.alpha ?? null,
        cargoRatioAlphaImprovement: ratio.alphaImprovement ?? null,
        improvementAlpha:
          ratio.alphaImprovement ?? item.improvementAlpha ?? null,
        alphaHps: ratio.alphaHps ?? item.alphaHps ?? null,
        alphaDyedHps: ratio.alphaDyedHps ?? item.alphaDyedHps ?? null,
        alphaUndyedHps: ratio.alphaUndyedHps ?? item.alphaUndyedHps ?? null,
        cargoRatioSnapshot,
        parcelSizeLabels,
      };
    });
  });
};

export const enrichLpgDeliveryItemsWithCargoRatio = async ({
  procurementId,
  quantityDeliveryTermId,
  items = [],
  unitLabel = LPG_UNIT,
  skipCargoRatio = false,
  token = null,
}) => {
  if (!items.length || !procurementId || !quantityDeliveryTermId) {
    return items;
  }

  const authToken = token || getAuthToken();

  if (skipCargoRatio) {
    const quantityTargets = await fetchQuantityInformationTargets(
      procurementId,
      quantityDeliveryTermId
    );
    if (!quantityTargets.length) {
      return items.map(item => ({
        ...item,
        quantityInformationCargoRatioId: null,
        cargoRatio: null,
        cargoRatioLabel: null,
      }));
    }
    const parcelSizeLabels = formatParcelSizes(quantityTargets, unitLabel);
    return items.map(item => ({
      ...applyQuantityDeliveryParcelSize(item, quantityTargets),
      quantityInformationCargoRatioId: null,
      cargoRatio: null,
      cargoRatioLabel: null,
      parcelSizeLabels,
    }));
  }

  const { rawItems } = await fetchIncotermTimeline(
    quantityDeliveryTermId,
    authToken
  );
  const incotermCargoRatios = collectCargoRatiosFromIncoterms(rawItems);

  if (incotermCargoRatios.length) {
    const quantityTargets =
      buildQuantityTargetsFromCargoRatios(incotermCargoRatios);
    const parcelSizeLabels = formatParcelSizes(quantityTargets, unitLabel);
    const ratioOptions = await fetchCargoRatioOptions();
    const ratiosByFk = selectionsByQuantityFk(quantityTargets, []);

    return expandItemsWithCargoRatios({
      items,
      quantityTargets,
      ratiosByFk,
      ratioOptions,
      parcelSizeLabels,
      unitLabel,
    });
  }

  const quantityTargets = await fetchQuantityInformationTargets(
    procurementId,
    quantityDeliveryTermId
  );

  if (!quantityTargets.length) {
    return items.map(item => ({
      ...item,
      quantityInformationCargoRatioId: null,
      cargoRatio: null,
      cargoRatioLabel: null,
    }));
  }

  const quantityFks = quantityTargets.map(
    target => target.quantityInformationFk
  );
  const parcelSizeLabels = formatParcelSizes(quantityTargets, unitLabel);
  const [ratioOptions, fallbackSelections] = await Promise.all([
    fetchCargoRatioOptions(),
    fetchAllCargoRatioSelections(quantityFks).then(selections =>
      filterSelectionsForParcel(selections, quantityFks)
    ),
  ]);
  const ratiosByFk = selectionsByQuantityFk(
    quantityTargets,
    fallbackSelections
  );

  return expandItemsWithCargoRatios({
    items,
    quantityTargets,
    ratiosByFk,
    ratioOptions,
    parcelSizeLabels,
    unitLabel,
  });
};

export const putCargoRatioPricing = async ({
  quantityInformationCargoRatioId,
  quantityInformationId,
  quantityIncotermFK,
  cargoRatio,
  parcelSize,
  alpha,
  alphaImprovement,
  alphaHps,
  alphaDyedHps,
  alphaUndyedHps,
  existing = null,
  token = null,
}) => {
  const snapshot = normalizeCargoRatioPricingFields(
    existing ?? {
      id: quantityInformationCargoRatioId,
      quantityInformationFK: quantityInformationId,
      quantityIncotermFK,
      cargoRatio,
      parcelSize,
      alpha,
      alphaImprovement,
      alphaHps,
      alphaDyedHps,
      alphaUndyedHps,
    }
  );

  const patch = {};
  if (quantityInformationId !== undefined) {
    patch.quantityInformationFK = quantityInformationId;
  }
  if (quantityIncotermFK !== undefined) {
    patch.quantityIncotermFK = quantityIncotermFK;
  }
  if (cargoRatio !== undefined) patch.cargoRatio = cargoRatio;
  if (parcelSize !== undefined) patch.parcelSize = parcelSize;
  if (alpha !== undefined) patch.alpha = alpha;
  if (alphaImprovement !== undefined) patch.alphaImprovement = alphaImprovement;
  if (alphaHps !== undefined) patch.alphaHps = alphaHps;
  if (alphaDyedHps !== undefined) patch.alphaDyedHps = alphaDyedHps;
  if (alphaUndyedHps !== undefined) patch.alphaUndyedHps = alphaUndyedHps;

  return putCargoRatioPricingShared({
    quantityInformationCargoRatioId,
    existing: snapshot,
    patch,
    token,
  });
};

export const resolveCargoRatioCounterTargets = async ({
  quantityInformationId,
  quantityIncotermId = null,
  selectedRatios = [],
  fallbackCargoRatioFk = null,
  groupRows = [],
  token = null,
  allowedSourceRatioKeys = null,
}) => {
  if (!selectedRatios.length) return [];

  const authToken = token || getAuthToken();
  const fkByRatio = new Map();
  const allFks = [];
  const allowedSourceKeySet = !allowedSourceRatioKeys?.length
    ? null
    : new Set(
        (allowedSourceRatioKeys || [])
          .map(key => getCargoRatioKey(key))
          .filter(Boolean)
      );
  const scopedRows = groupRows.filter(row => {
    const sameQi =
      String(row?.quantityInformationId ?? '') ===
      String(quantityInformationId ?? '');
    if (!sameQi) return false;
    if (!quantityIncotermId) return true;
    return (
      String(row?.quantityIncotermId ?? '') === String(quantityIncotermId ?? '')
    );
  });

  const rememberFk = (cargoRatio, fk, { respectAllowed = false } = {}) => {
    if (fk === null || fk === undefined || fk === '') return;
    allFks.push(fk);
    if (cargoRatio === null || cargoRatio === undefined || cargoRatio === '') {
      return;
    }
    const key = getCargoRatioKey(cargoRatio);
    if (
      respectAllowed &&
      allowedSourceKeySet &&
      !allowedSourceKeySet.has(key)
    ) {
      return;
    }
    if (!fkByRatio.has(key)) fkByRatio.set(key, fk);
  };

  scopedRows.forEach(row => {
    rememberFk(row?.cargoRatio, row?.quantityInformationCargoRatioId, {
      respectAllowed: true,
    });
  });

  if (fallbackCargoRatioFk) {
    rememberFk(null, fallbackCargoRatioFk);
  }

  try {
    if (authToken && quantityInformationId) {
      const selections = await fetchAllCargoRatioSelections([
        quantityInformationId,
      ]);

      selections.forEach(selection => {
        const selectionIncoterm = pick(
          selection,
          'quantityIncotermFK',
          'QuantityIncotermFK'
        );
        if (
          quantityIncotermId &&
          selectionIncoterm &&
          String(selectionIncoterm) !== String(quantityIncotermId)
        ) {
          return;
        }
        const cargoRatio = pick(selection, 'cargoRatio', 'CargoRatio');
        const selectionId = pick(selection, 'id', 'Id');
        rememberFk(cargoRatio, selectionId, { respectAllowed: true });
      });
    }
  } catch {
    void 0;
  }

  const uniqueFks = [...new Set(allFks.map(String))];
  if (!uniqueFks.length) return [];

  const usedFks = new Set();

  return selectedRatios
    .map(ratio => {
      const cargoRatio = Number(getCargoRatioKey(ratio?.id ?? ratio));
      if (!Number.isFinite(cargoRatio) || cargoRatio < 1 || cargoRatio > 5) {
        return null;
      }

      const ratioKey = getCargoRatioKey(cargoRatio);
      let quantityInformationCargoRatioFK = fkByRatio.get(ratioKey) ?? null;

      if (
        quantityInformationCargoRatioFK !== null &&
        quantityInformationCargoRatioFK !== undefined
      ) {
        usedFks.add(String(quantityInformationCargoRatioFK));
        return {
          cargoRatio,
          quantityInformationCargoRatioFK,
          isExactSourceMatch: true,
        };
      }

      quantityInformationCargoRatioFK = uniqueFks.find(
        fk => !usedFks.has(String(fk))
      );

      if (!quantityInformationCargoRatioFK) return null;
      usedFks.add(String(quantityInformationCargoRatioFK));

      return {
        cargoRatio,
        quantityInformationCargoRatioFK,
        isExactSourceMatch: false,
      };
    })
    .filter(Boolean);
};

export const deleteCargoRatioMasterSelections = async ({
  selectionIds = [],
  token = null,
}) => {
  const authToken = token || getAuthToken();
  const uniqueIds = [...new Set(selectionIds.filter(Boolean).map(String))];
  if (!authToken || !uniqueIds.length) return;

  await Promise.all(
    uniqueIds.map(async id => {
      try {
        await fetch(`${api.API_URL}/quantity-information-cargo-ratio/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
      } catch {
        void 0;
      }
    })
  );
};
